import {
  buildDomainMessageActionConfig,
  DOMAIN_MESSAGE_ACTION_CONTRACT,
} from './centralModel';

function definitionId(value, definitions) {
  const base = String(value || 'parent-message')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '') || 'parent-message';
  const ids = new Set(definitions.map((definition) => definition.id));
  let id = base;
  let suffix = 2;
  while (ids.has(id)) {
    id = `${base}-${suffix}`;
    suffix += 1;
  }
  return id;
}

function definitionPayload(input, id) {
  const label = String(input?.label || '').trim();
  if (!label) throw new Error('Parent message name is required');
  return {
    id,
    label,
    active: input?.active !== false,
    sortOrder: Number.isFinite(input?.sortOrder) ? input.sortOrder : 0,
    message: buildDomainMessageActionConfig(input?.message),
  };
}

export function readDomainParentMessages(domain) {
  const stored = domain?.parentMessages || domain || {};
  const definitions = Array.isArray(stored.definitions)
    ? stored.definitions
      .filter((definition) => definition && typeof definition.id === 'string')
      .slice(0, DOMAIN_MESSAGE_ACTION_CONTRACT.maxDefinitions)
      .sort((left, right) => (
        (left.sortOrder ?? 0) - (right.sortOrder ?? 0)
        || String(left.label || '').localeCompare(String(right.label || ''))
      ))
    : [];
  const defaultId = definitions.some((definition) => definition.id === stored.defaultId)
    ? stored.defaultId
    : null;
  return {
    version: DOMAIN_MESSAGE_ACTION_CONTRACT.version,
    defaultId,
    definitions,
  };
}

export function listDomainMessageActions(domain) {
  return readDomainParentMessages(domain).definitions;
}

export function createDomainMessageAction(parentMessages, input) {
  const current = readDomainParentMessages(parentMessages);
  if (current.definitions.length >= DOMAIN_MESSAGE_ACTION_CONTRACT.maxDefinitions) {
    throw new Error(
      `A Domain can have up to ${DOMAIN_MESSAGE_ACTION_CONTRACT.maxDefinitions} parent messages`,
    );
  }
  const actionId = definitionId(input?.label, current.definitions);
  return {
    actionId,
    parentMessages: {
      ...current,
      definitions: [
        ...current.definitions,
        definitionPayload(input, actionId),
      ],
    },
  };
}

export function updateDomainMessageAction(parentMessages, actionId, input) {
  if (!actionId) throw new Error('Parent message action id is required');
  const current = readDomainParentMessages(parentMessages);
  if (!current.definitions.some((definition) => definition.id === actionId)) {
    throw new Error(`Parent message not found: ${actionId}`);
  }
  return {
    ...current,
    definitions: current.definitions.map((definition) => (
      definition.id === actionId
        ? definitionPayload(input, actionId)
        : definition
    )),
  };
}

export function deleteDomainMessageAction(parentMessages, actionId) {
  if (!actionId) throw new Error('Parent message action id is required');
  const current = readDomainParentMessages(parentMessages);
  if (!current.definitions.some((definition) => definition.id === actionId)) {
    throw new Error(`Parent message not found: ${actionId}`);
  }
  return {
    ...current,
    defaultId: current.defaultId === actionId ? null : current.defaultId,
    // Message definition IDs can be referenced by historical events and audit
    // records. Removing the editor-visible definition must therefore retire it
    // rather than erase or reuse its stable key.
    definitions: current.definitions.map((definition) => (
      definition.id === actionId
        ? { ...definition, active: false, retired: true }
        : definition
    )),
  };
}

export function readDomainMessageAction(action) {
  return {
    label: action?.label || '',
    active: action?.active !== false,
    message: action?.message || buildDomainMessageActionConfig({
      subject: action?.emailSubject || '',
      body: action?.emailTemplate || action?.telegramTemplate || '',
      buttons: action?.messageActions || [],
    }),
  };
}
